import { Component, OnInit, HostListener } from '@angular/core';
import { AvoirService, Avoir } from '../service/avoir.service';
import { Router } from '@angular/router';
import { DatePipe } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { ReturnOrderComponent } from '../return-order/return-order.component';
import { AuthService } from '../service/auth.service';

@Component({
  selector: 'app-avoir-list',
  templateUrl: './avoir-list.component.html',
  styleUrls: ['./avoir-list.component.css'],
  providers: [DatePipe]
})
export class AvoirListComponent implements OnInit {
  avoirs: Avoir[] = [];
  isLoading = false;
  keyword = '';
  selectedDate = '';
  
  // Pagination
  currentPage = 0;
  pageSize = 10;
  totalElements = 0;
  totalPages = 0;

  // Dropdown action menu
  openDropdownId: number | null = null;

  toggleDropdown(id: number, event: Event): void {
    event.stopPropagation();
    this.openDropdownId = this.openDropdownId === id ? null : id;
  }

  closeDropdown(): void {
    this.openDropdownId = null;
  }

  @HostListener('document:click')
  onDocumentClick(): void {
    this.openDropdownId = null;
  }

  // Custom Confirmation Dialog State
  isConfirmVisible = false;
  confirmTitle = '';
  confirmMessage = '';
  confirmType: 'info' | 'warning' | 'danger' = 'info';
  confirmAction: (() => void) | null = null;
  confirmBtnText = 'Confirmer';

  constructor(
    private avoirService: AvoirService,
    private router: Router,
    private datePipe: DatePipe,
    private dialog: MatDialog,
    private authService: AuthService
  ) {}

  openConfirm(title: string, message: string, type: 'info' | 'warning' | 'danger', action: () => void, btnText: string = 'Confirmer'): void {
    this.confirmTitle = title;
    this.confirmMessage = message;
    this.confirmType = type;
    this.confirmAction = action;
    this.confirmBtnText = btnText;
    this.isConfirmVisible = true;
  }

  handleConfirm(): void {
    if (this.confirmAction) {
      this.confirmAction();
    }
    this.isConfirmVisible = false;
  }

  handleCancel(): void {
    this.isConfirmVisible = false;
    this.confirmAction = null;
  }

  ngOnInit(): void {
    this.loadAvoirs();
  }

  loadAvoirs(): void {
    this.isLoading = true;
    this.avoirService.getPagedAvoirs(this.currentPage, this.pageSize, this.keyword, this.selectedDate)
      .subscribe({
        next: (response) => {
          this.avoirs = response.content;
          this.totalElements = response.totalElements;
          this.totalPages = response.totalPages;
          this.isLoading = false;
        },
        error: (err) => {
          console.error('Error loading avoirs:', err);
          this.isLoading = false;
        }
      });
  }

  onSearch(): void {
    this.currentPage = 0;
    this.loadAvoirs();
  }

  onDateChange(): void {
    this.currentPage = 0;
    this.loadAvoirs();
  }

  onPageChange(page: number): void {
    if (page >= 0 && page < this.totalPages) {
      this.currentPage = page;
      this.loadAvoirs();
    }
  }

  get pendingCount(): number {
    return this.avoirs.filter(a => !a.status || a.status === 'PENDING').length;
  }

  get approvedCount(): number {
    return this.avoirs.filter(a => a.status === 'APPROVED').length;
  }

  get totalRefunded(): number {
    return this.avoirs.reduce((sum, a) => sum + (a.totalRefund || 0), 0);
  }

  viewDetails(id: number): void {
    this.router.navigate(['/avoir-detail', id]);
  }

  isAdmin(): boolean {
    const user = this.authService.getUser();
    return user && user.role === 'ADMIN';
  }

  isAuthor(avoir: Avoir): boolean {
    const user = this.authService.getUser();
    return user && (user.username === avoir.createdBy || !avoir.createdBy);
  }

  approveAvoirManual(id: number, event: Event): void {
    event.stopPropagation();
    this.openConfirm(
      'Approuver l\'Avoir',
      'Voulez-vous approuver cet avoir ? Cela validera définitivement le retour en stock et le remboursement financier. Cette action est irréversible.',
      'info',
      () => {
        this.avoirService.approveAvoir(id).subscribe({
          next: () => this.loadAvoirs(),
          error: (err: any) => {
            console.error(err);
            this.openConfirm(
              'Erreur de Validation',
              err.error?.message || 'Une erreur est survenue lors de la validation de l\'avoir.',
              'danger',
              () => {},
              'Compris'
            );
          }
        });
      },
      'Approuver'
    );
  }

  onEdit(avoir: Avoir, event: Event): void {
    event.stopPropagation();
    const isApproved = avoir.status === 'APPROVED' || avoir.status === 'COMPLETED';
    
    if (isApproved) {
      this.openConfirm(
        'Action Interdite',
        'Un avoir déjà validé et clos ne peut plus être modifié pour garantir la cohérence financière.',
        'warning',
        () => {},
        'Compris'
      );
      return;
    }

    const isAdmin = this.isAdmin();
    const canEdit = isAdmin || this.isAuthor(avoir);
    
    if (!canEdit) {
      this.openConfirm(
        'Accès Refusé',
        'Seul l\'auteur de cette demande ou un administrateur peut la modifier.',
        'warning',
        () => {},
        'Compris'
      );
      return;
    }

    this.openEditDialog(avoir);
  }

  private openEditDialog(avoir: Avoir): void {
    const dialogRef = this.dialog.open(ReturnOrderComponent, {
      width: '1000px',
      maxWidth: '95vw',
      data: { 
        order: avoir.originalOrder, 
        avoir: avoir 
      },
      disableClose: true
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.loadAvoirs();
      }
    });
  }


  deleteAvoir(avoir: Avoir, event: Event): void {
    event.stopPropagation();
    const isApproved = avoir.status === 'APPROVED' || avoir.status === 'COMPLETED';

    if (isApproved) {
      this.openConfirm(
        'Action Interdite',
        'Un avoir déjà validé ne peut plus être supprimé pour garantir l\'intégrité des données historiques.',
        'warning',
        () => {},
        'Compris'
      );
      return;
    }

    const isAdmin = this.isAdmin();
    if (!isAdmin && !this.isAuthor(avoir)) {
      this.openConfirm(
        'Accès Refusé',
        'Seul l\'auteur ou un administrateur peut supprimer cette demande.',
        'warning',
        () => {},
        'Compris'
      );
      return;
    }

    this.openConfirm(
      'Supprimer l\'Avoir',
      'Êtes-vous sûr de vouloir supprimer cette demande d\'avoir ?',
      'danger',
      () => {
        this.avoirService.deleteAvoir(avoir.id!).subscribe({
          next: () => this.loadAvoirs(),
          error: (err) => console.error(err)
        });
      },
      'Supprimer'
    );
  }

  formatDate(date: any): string {
    return this.datePipe.transform(date, 'dd/MM/yyyy HH:mm') || '';
  }
}
